import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  TrendingUp, 
  Users, 
  Clock,
  Search,
  Loader2,
  FileText,
  BarChart3,
  Target,
  Activity,
  Code,
  Send,
  Bot,
  User,
  ChevronDown,
  ChevronRight,
  Download,
  Presentation
} from 'lucide-react';

const SafetyAnalyst = () => {
  const { user, isAuthenticated } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState(0);
  const [expandedSQL, setExpandedSQL] = useState({});
  const [defaultQuestions, setDefaultQuestions] = useState([]);
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [slideLoading, setSlideLoading] = useState(false);
  const [slideProgress, setSlideProgress] = useState({});
  const [slideGenerationId, setSlideGenerationId] = useState(null);
  const [slideCompletionNotified, setSlideCompletionNotified] = useState(false);
  const [messageSlideStates, setMessageSlideStates] = useState({}); // Track slide state per message
  const messagesEndRef = useRef(null);

  // Fetch safety questions from API
  useEffect(() => {
    const fetchSafetyQuestions = async () => {
      try {
        const response = await fetch('/api/safety-analyst/questions', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.questions) {
            setDefaultQuestions(data.questions);
          }
        }
      } catch (error) {
        console.error('Failed to fetch safety questions:', error);
        // Fallback to empty array if API fails
        setDefaultQuestions([]);
      }
    };

    fetchSafetyQuestions();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (questionText) => {
    if (!questionText.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: questionText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setLoadingPhase(0);

    // Start the loading sequence
    let phaseInterval = setInterval(() => {
      setLoadingPhase(prev => {
        if (prev < 3) {
          return prev + 1;
        } else {
          clearInterval(phaseInterval);
          return prev;
        }
      });
    }, 10000); // 10 seconds per phase (40 seconds total)

    try {
      const response = await fetch('/api/safety-analyst/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ question: questionText })
      });

      const data = await response.json();

      if (data.success) {
        const botMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: data.explanation || data.analysis,
          sqlQuery: data.sqlQuery,
          data: data.data,
          columns: data.columns || (data.data && data.data.length > 0 ? Object.keys(data.data[0]) : []),
          timestamp: new Date()
        };

        setMessages(prev => [...prev, botMessage]);
        
        // Set follow-up questions if available
        if (data.followUpQuestions && data.followUpQuestions.length > 0) {
          setFollowUpQuestions(data.followUpQuestions);
        } else {
          setFollowUpQuestions([]);
        }
      } else {
        const errorMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: `Error: ${data.error || 'Failed to analyze safety data'}`,
          error: true,
          timestamp: new Date()
        };

        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (err) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: `Network error: ${err.message}`,
        error: true,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      setLoadingPhase(0);
      if (phaseInterval) {
        clearInterval(phaseInterval);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const generateSlides = async (reportData, messageId) => {
    try {
      console.log('🎬 Initiating slide generation for Safety Analyst report');
      console.log('📊 Report data:', reportData);
      console.log('🆔 Message ID:', messageId);
      setSlideLoading(true);
      setSlideProgress({ elapsed: 0, remaining: 120, progress: 0, completed: false });
      setSlideCompletionNotified(false); // Reset notification flag
      
      // Set slide state for this specific message
      setMessageSlideStates(prev => ({
        ...prev,
        [messageId]: {
          loading: true,
          progress: { elapsed: 0, remaining: 120, progress: 0, completed: false },
          generationId: null,
          completed: false
        }
      }));
      
      const requestBody = {
        report: {
          ...reportData,
          analysis: reportData.analysis || messages.find(m => m.type === 'bot' && m.id === messageId)?.content || '',
          explanation: messages.find(m => m.type === 'bot' && m.id === messageId)?.content || ''
        },
        structuredData: { safety: messages.filter(m => m.type === 'bot' && m.data) },
        metadata: { totalDataPoints: messages.filter(m => m.type === 'bot' && m.data).length },
        originalQuery: messages.find(m => m.type === 'user')?.content || 'Safety Analysis'
      };
      
      console.log('📤 Sending slide generation request:', requestBody);
      console.log('🔑 Token:', localStorage.getItem('token') ? 'Present' : 'Missing');
      
      const response = await fetch('/api/safety-analyst/generate-slides', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody)
      });
      
      console.log('📥 Response status:', response.status);
      console.log('📥 Response ok:', response.ok);

      if (response.ok) {
        const result = await response.json();
        if (result.generationId) {
          console.log(`🎬 Slide generation started with ID: ${result.generationId}`);
          setSlideGenerationId(result.generationId);
          
          // Update message-specific state
          setMessageSlideStates(prev => ({
            ...prev,
            [messageId]: {
              ...prev[messageId],
              generationId: result.generationId
            }
          }));
          
          // Start progress tracking timer
          const startTime = Date.now();
          const maxTime = 300; // 5 minutes for large slide decks
          
          const progressInterval = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            const remaining = Math.max(0, maxTime - elapsed);
            const progress = Math.min(95, (elapsed / maxTime) * 95); // Cap at 95% until actual completion
            
            console.log(`⏱️ Progress update: ${elapsed}s elapsed, ${remaining}s remaining, ${progress}% complete`);
            
            setSlideProgress({ 
              elapsed, 
              remaining, 
              progress,
              completed: false 
            });
            
            // Update message-specific progress
            setMessageSlideStates(prev => ({
              ...prev,
              [messageId]: {
                ...prev[messageId],
                progress: { elapsed, remaining, progress, completed: false }
              }
            }));
            
            // Don't auto-complete after timer - keep waiting for actual API response
            if (elapsed >= maxTime) {
              clearInterval(progressInterval);
              // Keep loading state - still waiting for API
            }
          }, 1000);
          
          // Poll for completion status
          let statusInterval;
          const pollSlideStatus = async () => {
            try {
              const statusResponse = await fetch(`/api/safety-analyst/slide-status/${result.generationId}`);
              if (statusResponse.ok) {
                const statusData = await statusResponse.json();
                if (statusData.status === 'completed' && !slideCompletionNotified) {
                  console.log('✅ Slide generation completed - showing success notification');
                  
                  // Clear both intervals immediately
                  clearInterval(progressInterval);
                  clearInterval(statusInterval);
                  
                  setSlideProgress({
                    elapsed: Math.floor((Date.now() - startTime) / 1000),
                    remaining: 0,
                    progress: 100,
                    completed: true,
                    gammaUrl: statusData.gammaUrl,
                    exportUrl: statusData.exportUrl
                  });
                  setSlideLoading(false);
                  setSlideCompletionNotified(true); // Prevent duplicate notifications
                  
                  // Update message-specific state with completion
                  setMessageSlideStates(prev => ({
                    ...prev,
                    [messageId]: {
                      ...prev[messageId],
                      loading: false,
                      completed: true,
                      progress: {
                        elapsed: Math.floor((Date.now() - startTime) / 1000),
                        remaining: 0,
                        progress: 100,
                        completed: true,
                        gammaUrl: statusData.gammaUrl,
                        exportUrl: statusData.exportUrl
                      }
                    }
                  }));
                }
              }
            } catch (statusError) {
              console.error('Slide status check failed:', statusError);
            }
          };
          
          // Check status immediately, then every 10 seconds
          pollSlideStatus(); // First check immediately
          statusInterval = setInterval(pollSlideStatus, 10000);
          
          // Keep polling indefinitely - no timeout stop
          // User can refresh page if needed, but we'll keep checking for completion
          
        } else {
          console.error('Failed to start slide generation');
          setSlideLoading(false);
        }
      } else {
        throw new Error('Slide generation failed');
      }
    } catch (error) {
      console.error('❌ Slide generation error:', error);
      console.error('❌ Error details:', error.message);
      console.error('❌ Error stack:', error.stack);
      setSlideLoading(false);
      
      // Reset message-specific state on error
      setMessageSlideStates(prev => ({
        ...prev,
        [messageId]: {
          ...prev[messageId],
          loading: false,
          error: error.message
        }
      }));
    }
  };

  const getLoadingMessage = () => {
    const messages = [
      "Analyzing safety data...",
      "Generating safety insights...",
      "Preparing safety recommendations...",
      "Generating follow-up questions..."
    ];
    return messages[loadingPhase] || messages[0];
  };

  const formatSQL = (sql) => {
    return sql
      .replace(/SELECT/gi, '\nSELECT')
      .replace(/FROM/gi, '\nFROM')
      .replace(/WHERE/gi, '\nWHERE')
      .replace(/JOIN/gi, '\nJOIN')
      .replace(/GROUP BY/gi, '\nGROUP BY')
      .replace(/ORDER BY/gi, '\nORDER BY')
      .replace(/LIMIT/gi, '\nLIMIT')
      .replace(/UNION/gi, '\nUNION')
      .replace(/WITH/gi, '\nWITH')
      .trim();
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    if (typeof value === 'number') return value.toLocaleString();
    return String(value);
  };

  const exportToCSV = (data, columns, title) => {
    if (!data || data.length === 0) return;

    try {
      // Create CSV content
      const header = columns.join(',');
      const rows = data.map(row => 
        columns.map(col => {
          const value = row[col];
          // Escape commas and quotes in CSV
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value || '';
        }).join(',')
      );
      
      const csvContent = [header, ...rows].join('\n');
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `safety-data-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-red-600" />
            <h1 className="text-3xl font-bold text-gray-900">Safety Analyst</h1>
          </div>
          <p className="text-gray-600 text-lg">
            AI-powered safety data analysis for construction projects
          </p>
        </div>

        {/* Chat Interface */}
        <div className="w-full flex-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-[700px] flex flex-col">
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-8">
              
              {messages.length === 0 && (
                <div className="text-center py-8">
                  {/* Default Questions */}
                  <div className="max-w-lg mx-auto">
                    <h4 className="text-md font-medium text-gray-800 mb-3">
                      Suggested Safety Questions
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {defaultQuestions.map((question, index) => (
                        <button
                          key={index}
                          onClick={() => sendMessage(question)}
                          className="text-left p-3 border border-gray-200 rounded-lg text-sm transition-colors bg-gray-50 hover:bg-red-50 hover:border-red-300 text-gray-700"
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {messages.map((message, index) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-4xl w-full rounded-lg px-4 py-3 ${
                      message.type === 'user'
                        ? 'bg-red-600 text-white'
                        : message.error
                        ? 'bg-red-50 text-red-800 border border-red-200'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <div className="flex items-start space-x-2">
                      {message.type === 'bot' && (
                        <Bot className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                      )}
                      {message.type === 'user' && (
                        <User className="h-5 w-5 text-white mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm break-words prose prose-sm max-w-none">
                          <div dangerouslySetInnerHTML={{ 
                            __html: message.content
                              .replace(/## (.*?)\n/g, '<h3 class="text-lg font-bold text-gray-900 mt-6 mb-3 border-b border-gray-200 pb-2">$1</h3>')
                              .replace(/### (.*?)\n/g, '<h4 class="text-md font-semibold text-gray-800 mt-4 mb-2">$1</h4>')
                              .replace(/- (.*?)\n/g, '<li class="ml-4 mb-2 text-gray-700 leading-relaxed">• $1</li>')
                              .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
                              .replace(/\*(.*?)\*/g, '<em class="italic text-gray-700">$1</em>')
                              .replace(/\n\n/g, '<br/><br/>')
                              .replace(/\n/g, '<br/>')
                          }} />
                        </div>

                        {/* SQL Query Display - Collapsible */}
                        {message.sqlQuery && (
                          <div className="mt-3">
                            <div className="bg-gray-50 rounded-lg border border-gray-200">
                              <div className="p-3">
                                <button
                                  onClick={() => setExpandedSQL(prev => ({ ...prev, [message.id]: !prev[message.id] }))}
                                  className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900"
                                >
                                  <span className="flex items-center">
                                    <Code className="h-4 w-4 mr-2" />
                                    {expandedSQL[message.id] ? 'Hide SQL Query' : 'Show SQL Query'}
                                  </span>
                                  <ChevronDown className={`h-4 w-4 transition-transform ${expandedSQL[message.id] ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {expandedSQL[message.id] && (
                                  <div className="mt-3 p-3 bg-gray-800 rounded text-green-400 text-xs font-mono">
                                    <div className="overflow-x-auto">
                                      <pre className="whitespace-pre-wrap break-words">{formatSQL(message.sqlQuery)}</pre>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Data Results */}
                        {message.data && message.data.length > 0 && (
                          <div className="mt-3">
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    {message.columns.map((column, index) => (
                                      <th
                                        key={index}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                      >
                                        {column.replace(/_/g, ' ')}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {message.data.slice(0, 100).map((row, rowIndex) => (
                                    <tr key={rowIndex} className="hover:bg-gray-50">
                                      {message.columns.map((column, colIndex) => (
                                        <td
                                          key={colIndex}
                                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                                        >
                                          {formatValue(row[column])}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {message.data.length > 100 && (
                                <div className="px-6 py-3 bg-gray-50 text-sm text-gray-500">
                                  Showing first 100 of {message.data.length} records
                                </div>
                              )}
                              
                              {/* Export CSV Button */}
                              <div className="mt-3 flex justify-end">
                                <button
                                  onClick={() => exportToCSV(message.data, message.columns, message.content)}
                                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                  title="Export data to CSV"
                                >
                                  <Download size={16} className="mr-2" />
                                  CSV
                                </button>
                              </div>
                              
                              {/* Slide Generation Button or Completion UI */}
                              {messageSlideStates[message.id]?.completed ? (
                                <div className="flex items-center space-x-2">
                                  <div className="flex items-center text-green-600 text-sm">
                                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Presentation Generated!
                                  </div>
                                  {messageSlideStates[message.id]?.progress?.gammaUrl && (
                                    <button
                                      onClick={() => window.open(messageSlideStates[message.id].progress.gammaUrl, '_blank')}
                                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-white border border-green-300 rounded-md hover:bg-green-50"
                                    >
                                      🔗 View Slides
                                    </button>
                                  )}
                                  {messageSlideStates[message.id]?.progress?.exportUrl && (
                                    <a
                                      href={messageSlideStates[message.id].progress.exportUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-white border border-green-300 rounded-md hover:bg-green-50"
                                      title="Open PPTX in new tab"
                                    >
                                      💾 Open PPTX
                                    </a>
                                  )}
                                </div>
                              ) : (
                                <div className="flex flex-col items-end space-y-2">
                                  <button
                                    onClick={() => generateSlides({ title: 'Safety Analysis Report', data: message.data, columns: message.columns }, message.id)}
                                    disabled={slideLoading || (messageSlideStates[message.id]?.loading)}
                                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {(slideLoading || messageSlideStates[message.id]?.loading) ? (
                                      <>
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        Generating...
                                      </>
                                    ) : (
                                      <>
                                        <Presentation className="h-4 w-4 mr-2" />
                                        Generate Slides
                                      </>
                                    )}
                                  </button>
                                  
                                  {/* Progress Bar - Show during generation */}
                                  {(slideLoading || messageSlideStates[message.id]?.loading) && messageSlideStates[message.id]?.progress && (
                                    <div className="w-48 text-sm text-gray-600">
                                      <div className="flex items-center justify-between mb-1">
                                        <span>⏱️ {messageSlideStates[message.id].progress.elapsed || 0}s elapsed</span>
                                        <span>⏳ {messageSlideStates[message.id].progress.remaining || 120}s remaining</span>
                                      </div>
                                      <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div 
                                          className="bg-red-600 h-2 rounded-full transition-all duration-300 ease-out" 
                                          style={{ width: `${messageSlideStates[message.id].progress.progress || 0}%` }}
                                        ></div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Follow-up Questions */}
              {followUpQuestions.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100 mb-2">
                  <div className="flex items-center space-x-2 mb-3">
                    <ChevronRight className="h-4 w-4 text-red-600" />
                    <span className="text-sm font-medium text-gray-700">Follow-up questions:</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {followUpQuestions.map((question, index) => (
                      <button
                        key={index}
                        onClick={() => sendMessage(question)}
                        className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-full text-xs font-medium transition-colors border border-red-200"
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-red-50 rounded-lg px-4 py-3 border border-red-200">
                    <div className="flex items-center space-x-2">
                      <Loader2 className="h-5 w-5 text-red-500 animate-spin" />
                      <span className="text-sm text-red-700 font-medium">
                        {getLoadingMessage()}
                      </span>
                    </div>
                    <div className="flex space-x-1 mt-2">
                      {[0, 1, 2, 3].map((phase) => (
                        <div
                          key={phase}
                          className={`h-1 w-8 rounded-full transition-all duration-300 ${
                            phase <= loadingPhase ? 'bg-red-500' : 'bg-red-200'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Form */}
            <div className="border-t border-gray-200 p-4">
              <form onSubmit={handleSubmit} className="flex space-x-3">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about safety incidents, equipment inspections, training compliance..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Send className="h-4 w-4" />
                  <span>Send</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafetyAnalyst;